// frontend/src/data/countries.js
// Static country reference for the team-manager feature.
//
// Each country has:
//   - name: display name
//   - iso2: ISO 3166-1 alpha-2 (used to render Unicode flag emoji via regional indicators)
//   - iso3: ISO 3166-1 alpha-3 (matched against Sportradar persons.nationality)
//   - ioc:  IOC code (also matched against Sportradar persons.nationality;
//           defaults to iso3 when not overridden — see IOC_OVERRIDES)
//
// We don't change the DB schema for this — team_managers stores iso3 + ioc,
// and the UI looks up name + flag from these codes via the helpers below.

// ISO3 → IOC overrides. Only listed when IOC differs from ISO3.
const IOC_OVERRIDES = {
    DZA: 'ALG', ATG: 'ANT', BHS: 'BAH', BHR: 'BRN', BGD: 'BAN', BRB: 'BAR',
    BLZ: 'BIZ', BTN: 'BHU', BGR: 'BUL', BFA: 'BUR', KHM: 'CAM', CHL: 'CHI',
    CRI: 'CRC', HRV: 'CRO', DNK: 'DEN', SLV: 'ESA', GNQ: 'GEQ', DEU: 'GER',
    GRC: 'GRE', GRD: 'GRN', GTM: 'GUA', GIN: 'GUI', GNB: 'GBS', HTI: 'HAI',
    HND: 'HON', IDN: 'INA', IRN: 'IRI', KWT: 'KUW', LVA: 'LAT', LBN: 'LIB',
    LSO: 'LES', LBY: 'LBA', MDG: 'MAD', MWI: 'MAW', MYS: 'MAS', MRT: 'MTN',
    MUS: 'MRI', MCO: 'MON', MNG: 'MGL', MMR: 'MYA', NPL: 'NEP', NLD: 'NED',
    NIC: 'NCA', NER: 'NIG', NGA: 'NGR', OMN: 'OMA', PRY: 'PAR', PHL: 'PHI',
    PRT: 'POR', PRI: 'PUR', SAU: 'KSA', SVN: 'SLO', ZAF: 'RSA', LKA: 'SRI',
    SDN: 'SUD', CHE: 'SUI', TWN: 'TPE', TZA: 'TAN', ARE: 'UAE', URY: 'URU',
    VNM: 'VIE',
};

// Country list. Sorted alphabetically. iso2 codes used for flag emoji rendering.
const COUNTRY_LIST = [
    { name: 'Afghanistan', iso2: 'AF', iso3: 'AFG' },
    { name: 'Albania', iso2: 'AL', iso3: 'ALB' },
    { name: 'Algeria', iso2: 'DZ', iso3: 'DZA' },
    { name: 'Andorra', iso2: 'AD', iso3: 'AND' },
    { name: 'Angola', iso2: 'AO', iso3: 'AGO' },
    { name: 'Antigua and Barbuda', iso2: 'AG', iso3: 'ATG' },
    { name: 'Argentina', iso2: 'AR', iso3: 'ARG' },
    { name: 'Armenia', iso2: 'AM', iso3: 'ARM' },
    { name: 'Australia', iso2: 'AU', iso3: 'AUS' },
    { name: 'Austria', iso2: 'AT', iso3: 'AUT' },
    { name: 'Azerbaijan', iso2: 'AZ', iso3: 'AZE' },
    { name: 'Bahamas', iso2: 'BS', iso3: 'BHS' },
    { name: 'Bahrain', iso2: 'BH', iso3: 'BHR' },
    { name: 'Bangladesh', iso2: 'BD', iso3: 'BGD' },
    { name: 'Barbados', iso2: 'BB', iso3: 'BRB' },
    { name: 'Belarus', iso2: 'BY', iso3: 'BLR' },
    { name: 'Belgium', iso2: 'BE', iso3: 'BEL' },
    { name: 'Belize', iso2: 'BZ', iso3: 'BLZ' },
    { name: 'Benin', iso2: 'BJ', iso3: 'BEN' },
    { name: 'Bermuda', iso2: 'BM', iso3: 'BMU' },
    { name: 'Bhutan', iso2: 'BT', iso3: 'BTN' },
    { name: 'Bolivia', iso2: 'BO', iso3: 'BOL' },
    { name: 'Bosnia and Herzegovina', iso2: 'BA', iso3: 'BIH' },
    { name: 'Botswana', iso2: 'BW', iso3: 'BWA' },
    { name: 'Brazil', iso2: 'BR', iso3: 'BRA' },
    { name: 'Brunei Darussalam', iso2: 'BN', iso3: 'BRN' },
    { name: 'Bulgaria', iso2: 'BG', iso3: 'BGR' },
    { name: 'Burkina Faso', iso2: 'BF', iso3: 'BFA' },
    { name: 'Burundi', iso2: 'BI', iso3: 'BDI' },
    { name: 'Cambodia', iso2: 'KH', iso3: 'KHM' },
    { name: 'Cameroon', iso2: 'CM', iso3: 'CMR' },
    { name: 'Canada', iso2: 'CA', iso3: 'CAN' },
    { name: 'Cape Verde', iso2: 'CV', iso3: 'CPV' },
    { name: 'Cayman Islands', iso2: 'KY', iso3: 'CYM' },
    { name: 'Central African Republic', iso2: 'CF', iso3: 'CAF' },
    { name: 'Chad', iso2: 'TD', iso3: 'TCD' },
    { name: 'Chile', iso2: 'CL', iso3: 'CHL' },
    { name: 'China', iso2: 'CN', iso3: 'CHN' },
    { name: 'Chinese Taipei', iso2: 'TW', iso3: 'TWN' },
    { name: 'Colombia', iso2: 'CO', iso3: 'COL' },
    { name: 'Comoros', iso2: 'KM', iso3: 'COM' },
    { name: 'Congo', iso2: 'CG', iso3: 'COG' },
    { name: 'Cook Islands', iso2: 'CK', iso3: 'COK' },
    { name: 'Costa Rica', iso2: 'CR', iso3: 'CRI' },
    { name: 'Côte d\'Ivoire', iso2: 'CI', iso3: 'CIV' },
    { name: 'Croatia', iso2: 'HR', iso3: 'HRV' },
    { name: 'Cuba', iso2: 'CU', iso3: 'CUB' },
    { name: 'Cyprus', iso2: 'CY', iso3: 'CYP' },
    { name: 'Czech Republic', iso2: 'CZ', iso3: 'CZE' },
    { name: 'Democratic Republic of the Congo', iso2: 'CD', iso3: 'COD' },
    { name: 'Denmark', iso2: 'DK', iso3: 'DNK' },
    { name: 'Djibouti', iso2: 'DJ', iso3: 'DJI' },
    { name: 'Dominica', iso2: 'DM', iso3: 'DMA' },
    { name: 'Dominican Republic', iso2: 'DO', iso3: 'DOM' },
    { name: 'Ecuador', iso2: 'EC', iso3: 'ECU' },
    { name: 'Egypt', iso2: 'EG', iso3: 'EGY' },
    { name: 'El Salvador', iso2: 'SV', iso3: 'SLV' },
    { name: 'Equatorial Guinea', iso2: 'GQ', iso3: 'GNQ' },
    { name: 'Eritrea', iso2: 'ER', iso3: 'ERI' },
    { name: 'Estonia', iso2: 'EE', iso3: 'EST' },
    { name: 'Eswatini', iso2: 'SZ', iso3: 'SWZ' },
    { name: 'Ethiopia', iso2: 'ET', iso3: 'ETH' },
    { name: 'Fiji', iso2: 'FJ', iso3: 'FJI' },
    { name: 'Finland', iso2: 'FI', iso3: 'FIN' },
    { name: 'France', iso2: 'FR', iso3: 'FRA' },
    { name: 'Gabon', iso2: 'GA', iso3: 'GAB' },
    { name: 'Gambia', iso2: 'GM', iso3: 'GMB' },
    { name: 'Georgia', iso2: 'GE', iso3: 'GEO' },
    { name: 'Germany', iso2: 'DE', iso3: 'DEU' },
    { name: 'Ghana', iso2: 'GH', iso3: 'GHA' },
    { name: 'Great Britain', iso2: 'GB', iso3: 'GBR' },
    { name: 'Greece', iso2: 'GR', iso3: 'GRC' },
    { name: 'Grenada', iso2: 'GD', iso3: 'GRD' },
    { name: 'Guam', iso2: 'GU', iso3: 'GUM' },
    { name: 'Guatemala', iso2: 'GT', iso3: 'GTM' },
    { name: 'Guinea', iso2: 'GN', iso3: 'GIN' },
    { name: 'Guinea-Bissau', iso2: 'GW', iso3: 'GNB' },
    { name: 'Guyana', iso2: 'GY', iso3: 'GUY' },
    { name: 'Haiti', iso2: 'HT', iso3: 'HTI' },
    { name: 'Honduras', iso2: 'HN', iso3: 'HND' },
    { name: 'Hong Kong', iso2: 'HK', iso3: 'HKG' },
    { name: 'Hungary', iso2: 'HU', iso3: 'HUN' },
    { name: 'Iceland', iso2: 'IS', iso3: 'ISL' },
    { name: 'India', iso2: 'IN', iso3: 'IND' },
    { name: 'Indonesia', iso2: 'ID', iso3: 'IDN' },
    { name: 'Iran', iso2: 'IR', iso3: 'IRN' },
    { name: 'Iraq', iso2: 'IQ', iso3: 'IRQ' },
    { name: 'Ireland', iso2: 'IE', iso3: 'IRL' },
    { name: 'Israel', iso2: 'IL', iso3: 'ISR' },
    { name: 'Italy', iso2: 'IT', iso3: 'ITA' },
    { name: 'Jamaica', iso2: 'JM', iso3: 'JAM' },
    { name: 'Japan', iso2: 'JP', iso3: 'JPN' },
    { name: 'Jordan', iso2: 'JO', iso3: 'JOR' },
    { name: 'Kazakhstan', iso2: 'KZ', iso3: 'KAZ' },
    { name: 'Kenya', iso2: 'KE', iso3: 'KEN' },
    { name: 'Kiribati', iso2: 'KI', iso3: 'KIR' },
    { name: 'Kosovo', iso2: 'XK', iso3: 'XKX' },
    { name: 'Kuwait', iso2: 'KW', iso3: 'KWT' },
    { name: 'Kyrgyzstan', iso2: 'KG', iso3: 'KGZ' },
    { name: 'Laos', iso2: 'LA', iso3: 'LAO' },
    { name: 'Latvia', iso2: 'LV', iso3: 'LVA' },
    { name: 'Lebanon', iso2: 'LB', iso3: 'LBN' },
    { name: 'Lesotho', iso2: 'LS', iso3: 'LSO' },
    { name: 'Liberia', iso2: 'LR', iso3: 'LBR' },
    { name: 'Libya', iso2: 'LY', iso3: 'LBY' },
    { name: 'Liechtenstein', iso2: 'LI', iso3: 'LIE' },
    { name: 'Lithuania', iso2: 'LT', iso3: 'LTU' },
    { name: 'Luxembourg', iso2: 'LU', iso3: 'LUX' },
    { name: 'Madagascar', iso2: 'MG', iso3: 'MDG' },
    { name: 'Malawi', iso2: 'MW', iso3: 'MWI' },
    { name: 'Malaysia', iso2: 'MY', iso3: 'MYS' },
    { name: 'Maldives', iso2: 'MV', iso3: 'MDV' },
    { name: 'Mali', iso2: 'ML', iso3: 'MLI' },
    { name: 'Malta', iso2: 'MT', iso3: 'MLT' },
    { name: 'Marshall Islands', iso2: 'MH', iso3: 'MHL' },
    { name: 'Mauritania', iso2: 'MR', iso3: 'MRT' },
    { name: 'Mauritius', iso2: 'MU', iso3: 'MUS' },
    { name: 'Mexico', iso2: 'MX', iso3: 'MEX' },
    { name: 'Micronesia', iso2: 'FM', iso3: 'FSM' },
    { name: 'Moldova', iso2: 'MD', iso3: 'MDA' },
    { name: 'Monaco', iso2: 'MC', iso3: 'MCO' },
    { name: 'Mongolia', iso2: 'MN', iso3: 'MNG' },
    { name: 'Montenegro', iso2: 'ME', iso3: 'MNE' },
    { name: 'Morocco', iso2: 'MA', iso3: 'MAR' },
    { name: 'Mozambique', iso2: 'MZ', iso3: 'MOZ' },
    { name: 'Myanmar', iso2: 'MM', iso3: 'MMR' },
    { name: 'Namibia', iso2: 'NA', iso3: 'NAM' },
    { name: 'Nauru', iso2: 'NR', iso3: 'NRU' },
    { name: 'Nepal', iso2: 'NP', iso3: 'NPL' },
    { name: 'Netherlands', iso2: 'NL', iso3: 'NLD' },
    { name: 'New Zealand', iso2: 'NZ', iso3: 'NZL' },
    { name: 'Nicaragua', iso2: 'NI', iso3: 'NIC' },
    { name: 'Niger', iso2: 'NE', iso3: 'NER' },
    { name: 'Nigeria', iso2: 'NG', iso3: 'NGA' },
    { name: 'North Korea', iso2: 'KP', iso3: 'PRK' },
    { name: 'North Macedonia', iso2: 'MK', iso3: 'MKD' },
    { name: 'Norway', iso2: 'NO', iso3: 'NOR' },
    { name: 'Oman', iso2: 'OM', iso3: 'OMN' },
    { name: 'Pakistan', iso2: 'PK', iso3: 'PAK' },
    { name: 'Palau', iso2: 'PW', iso3: 'PLW' },
    { name: 'Palestine', iso2: 'PS', iso3: 'PSE' },
    { name: 'Panama', iso2: 'PA', iso3: 'PAN' },
    { name: 'Papua New Guinea', iso2: 'PG', iso3: 'PNG' },
    { name: 'Paraguay', iso2: 'PY', iso3: 'PRY' },
    { name: 'Peru', iso2: 'PE', iso3: 'PER' },
    { name: 'Philippines', iso2: 'PH', iso3: 'PHL' },
    { name: 'Poland', iso2: 'PL', iso3: 'POL' },
    { name: 'Portugal', iso2: 'PT', iso3: 'PRT' },
    { name: 'Puerto Rico', iso2: 'PR', iso3: 'PRI' },
    { name: 'Qatar', iso2: 'QA', iso3: 'QAT' },
    { name: 'Romania', iso2: 'RO', iso3: 'ROU' },
    { name: 'Russia', iso2: 'RU', iso3: 'RUS' },
    { name: 'Rwanda', iso2: 'RW', iso3: 'RWA' },
    { name: 'Saint Kitts and Nevis', iso2: 'KN', iso3: 'KNA' },
    { name: 'Saint Lucia', iso2: 'LC', iso3: 'LCA' },
    { name: 'Saint Vincent and the Grenadines', iso2: 'VC', iso3: 'VCT' },
    { name: 'Samoa', iso2: 'WS', iso3: 'WSM' },
    { name: 'San Marino', iso2: 'SM', iso3: 'SMR' },
    { name: 'Saudi Arabia', iso2: 'SA', iso3: 'SAU' },
    { name: 'Senegal', iso2: 'SN', iso3: 'SEN' },
    { name: 'Serbia', iso2: 'RS', iso3: 'SRB' },
    { name: 'Seychelles', iso2: 'SC', iso3: 'SYC' },
    { name: 'Sierra Leone', iso2: 'SL', iso3: 'SLE' },
    { name: 'Singapore', iso2: 'SG', iso3: 'SGP' },
    { name: 'Slovakia', iso2: 'SK', iso3: 'SVK' },
    { name: 'Slovenia', iso2: 'SI', iso3: 'SVN' },
    { name: 'Solomon Islands', iso2: 'SB', iso3: 'SLB' },
    { name: 'Somalia', iso2: 'SO', iso3: 'SOM' },
    { name: 'South Africa', iso2: 'ZA', iso3: 'ZAF' },
    { name: 'South Korea', iso2: 'KR', iso3: 'KOR' },
    { name: 'South Sudan', iso2: 'SS', iso3: 'SSD' },
    { name: 'Spain', iso2: 'ES', iso3: 'ESP' },
    { name: 'Sri Lanka', iso2: 'LK', iso3: 'LKA' },
    { name: 'Sudan', iso2: 'SD', iso3: 'SDN' },
    { name: 'Suriname', iso2: 'SR', iso3: 'SUR' },
    { name: 'Sweden', iso2: 'SE', iso3: 'SWE' },
    { name: 'Switzerland', iso2: 'CH', iso3: 'CHE' },
    { name: 'Syria', iso2: 'SY', iso3: 'SYR' },
    { name: 'Tajikistan', iso2: 'TJ', iso3: 'TJK' },
    { name: 'Tanzania', iso2: 'TZ', iso3: 'TZA' },
    { name: 'Thailand', iso2: 'TH', iso3: 'THA' },
    { name: 'Timor-Leste', iso2: 'TL', iso3: 'TLS' },
    { name: 'Togo', iso2: 'TG', iso3: 'TGO' },
    { name: 'Tonga', iso2: 'TO', iso3: 'TON' },
    { name: 'Trinidad and Tobago', iso2: 'TT', iso3: 'TTO' },
    { name: 'Tunisia', iso2: 'TN', iso3: 'TUN' },
    { name: 'Turkey', iso2: 'TR', iso3: 'TUR' },
    { name: 'Turkmenistan', iso2: 'TM', iso3: 'TKM' },
    { name: 'Tuvalu', iso2: 'TV', iso3: 'TUV' },
    { name: 'Uganda', iso2: 'UG', iso3: 'UGA' },
    { name: 'Ukraine', iso2: 'UA', iso3: 'UKR' },
    { name: 'United Arab Emirates', iso2: 'AE', iso3: 'ARE' },
    { name: 'United States', iso2: 'US', iso3: 'USA' },
    { name: 'Uruguay', iso2: 'UY', iso3: 'URY' },
    { name: 'Uzbekistan', iso2: 'UZ', iso3: 'UZB' },
    { name: 'Vanuatu', iso2: 'VU', iso3: 'VUT' },
    { name: 'Venezuela', iso2: 'VE', iso3: 'VEN' },
    { name: 'Vietnam', iso2: 'VN', iso3: 'VNM' },
    { name: 'Virgin Islands', iso2: 'VI', iso3: 'VIR' },
    { name: 'Yemen', iso2: 'YE', iso3: 'YEM' },
    { name: 'Zambia', iso2: 'ZM', iso3: 'ZMB' },
    { name: 'Zimbabwe', iso2: 'ZW', iso3: 'ZWE' },
];

// Each country gets its derived IOC code attached for easy lookup.
export const COUNTRIES = COUNTRY_LIST.map(c => ({
    name: c.name,
    iso2: c.iso2,
    iso3: c.iso3,
    ioc: IOC_OVERRIDES[c.iso3] || c.iso3,
}));

const byNameLower = new Map(COUNTRIES.map(c => [c.name.toLowerCase(), c]));
const byIso3 = new Map(COUNTRIES.map(c => [c.iso3, c]));
const byIoc = new Map(COUNTRIES.map(c => [c.ioc, c]));

/** Look up a country by exact name (case-insensitive). */
export const getCountryByName = (name) => {
    if (!name) return null;
    return byNameLower.get(name.trim().toLowerCase()) || null;
};

/** Look up a country by either ISO3 or IOC code. */
export const getCountryByCodes = (iso3, ioc) => {
    return (iso3 && byIso3.get(iso3)) || (ioc && byIoc.get(ioc)) || null;
};

/**
 * Display name from stored ISO3 / IOC codes.
 * Falls back to whichever code is present when both are unrecognized.
 */
export const getCountryNameFromCodes = (iso3, ioc) => {
    const c = getCountryByCodes(iso3, ioc);
    if (c) return c.name;
    return [iso3, ioc].filter(Boolean).join(' / ') || '';
};

/**
 * Public flag-image URL for a country. We use flagcdn.com PNGs because Unicode
 * flag emoji don't render on Windows (no glyphs in default fonts).
 *
 * size: one of 20, 40, 80, 160, 320, 640, 1280, 2560 (width in px).
 * Aspect ratio is ~4:3.
 */
export const getCountryFlagUrl = (iso3, ioc, size = 40) => {
    const c = getCountryByCodes(iso3, ioc);
    if (!c?.iso2) return null;
    return `https://flagcdn.com/w${size}/${c.iso2.toLowerCase()}.png`;
};
